import type { CSSProperties } from 'react'
import type { ClubHeroSettings } from '@/lib/domain/club'

/**
 * Geometria dello sfondo dell'header della pagina club.
 *
 * Perché non basta `object-fit: cover` + `object-position`: con `cover` la foto
 * combacia **esattamente** con un asse del riquadro, quindi su quell'asse il focus
 * non ha spazio e trascinare non sposta nulla. Qui invece l'immagine è un livello
 * **più grande del riquadro su entrambi gli assi** e viene mossa con una
 * traslazione: il focus sposta davvero (e lo zoom ingrandisce) in ogni caso.
 *
 * Lo stesso modulo serve la pagina (rendering) e l'editor (anteprima live), così le
 * due cose non possono divergere.
 */

/** Di quanto la foto sborda per lato, in % del riquadro: è lo spazio di manovra. */
export const HERO_LAYER_SLACK = 12

/** Gradiente di lega **sopra** la foto: quasi pieno a destra, più chiaro a sinistra. */
export const HERO_OVERLAY_GRADIENT =
  'linear-gradient(to left, rgba(22, 33, 51, 0.93) 0%, rgba(30, 58, 95, 0.78) 45%, rgba(45, 90, 142, 0.55) 100%)'

/**
 * Spostamento del livello, in % della **propria** dimensione (`translate` in % si
 * riferisce all'elemento stesso). Il livello misura `100 + 2·SLACK`, quindi lo
 * spazio per lato è `SLACK / (100 + 2·SLACK)` della sua larghezza.
 */
function panPercent(focus: number): number {
  const span = HERO_LAYER_SLACK / (100 + HERO_LAYER_SLACK * 2)
  return ((50 - Math.min(100, Math.max(0, focus))) / 50) * span * 100
}

/** Stile del livello immagine: oversize sui due assi + traslazione del focus + zoom. */
export function heroImageStyle(hero: ClubHeroSettings): CSSProperties {
  return {
    position: 'absolute',
    inset: `-${HERO_LAYER_SLACK}%`,
    backgroundImage: `url(${hero.imageUrl})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    willChange: 'transform',
    transform: `translate(${panPercent(hero.positionX).toFixed(2)}%, ${panPercent(hero.positionY).toFixed(2)}%) scale(${hero.zoom})`,
  }
}

/** Applica lo stile al livello già presente nel DOM (anteprima live dell'editor). */
export function applyHeroImageStyle(el: HTMLElement, hero: ClubHeroSettings): void {
  const style = heroImageStyle(hero)
  el.style.transform = String(style.transform ?? '')
  el.style.backgroundImage = String(style.backgroundImage ?? '')
}
